import { memo } from 'react';
import { Group, Text, ActionIcon, Box, Badge, Tooltip } from '@mantine/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical, IconTrash, IconExternalLink } from '@tabler/icons-react';
import { ChildThumbnail } from './ChildThumbnail';
import type { ChildKind } from '../AppContentUtils';

/**
 * The sortable id: a kind-qualified key (AppContentUtils.childKey) for a mixed
 * category list — listlist and media ids may collide numerically — or a bare
 * list id for the seasons editor.
 */
interface SortableChildItemProps<TId extends string | number> {
  id: TId;
  index: number;
  title: string;
  /** Second line under the title (series "Series · N seasons", video duration · date, ids). */
  subtitle?: string | null;
  displayId?: string;
  /** Kind chip for a mixed list (Series | Video | Live stream); omitted when every child is the same kind. */
  kindLabel?: string;
  kindColor?: string;
  /** 16:9 art; when `kind` is given the row renders a thumbnail box even without art. */
  kind?: ChildKind;
  thumbnailUrl?: string | null;
  /** A live stream on air right now (decoration only). */
  onAir?: boolean;
  /** Shows an "Inactive" badge (the child is hidden from the app). */
  inactive?: boolean;
  /** Greys the row for a child the account no longer lists; Remove still works. */
  unavailable?: boolean;
  /** Briefly emphasizes a row that was just added. */
  highlighted?: boolean;
  onRemove: (id: TId) => void;
  onNavigate?: (id: TId) => void;
}

function SortableChildItemInner<TId extends string | number>({
  id,
  index,
  title,
  subtitle,
  displayId,
  kindLabel,
  kindColor,
  kind,
  thumbnailUrl,
  onAir,
  inactive,
  unavailable,
  highlighted,
  onRemove,
  onNavigate,
}: SortableChildItemProps<TId>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: [transition, 'background-color 600ms ease'].filter(Boolean).join(', '),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      data-child-key={String(id)}
      py={6}
      px="sm"
      bg={isDragging ? 'gray.0' : highlighted ? 'yellow.0' : undefined}
      bd="1px solid var(--mantine-color-gray-2)"
      style={{
        ...style,
        borderRadius: 'var(--mantine-radius-sm)',
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {/* Drag handle — also the keyboard target: focus it, Space picks up, arrows move, Space drops */}
          <Tooltip label="Drag, or focus and use Space + arrow keys" openDelay={600} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Reorder ${title}`}
              style={{ cursor: 'grab', touchAction: 'none' }}
              {...attributes}
              {...listeners}
            >
              <IconGripVertical size={16} />
            </ActionIcon>
          </Tooltip>

          {/* Position number */}
          <Text size="sm" c="dimmed" fw={600} w={28} ta="center" style={{ flexShrink: 0 }}>
            {index + 1}
          </Text>

          {kind && (
            <ChildThumbnail url={thumbnailUrl ?? null} kind={kind} title={title} width={96} onAir={onAir} dimmed={unavailable} />
          )}

          {/* Title + second line */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text
                size="sm"
                fw={500}
                truncate
                c={unavailable ? 'dimmed' : undefined}
                fs={unavailable ? 'italic' : undefined}
                style={{ minWidth: 0 }}
              >
                {title}
              </Text>
              {inactive && !unavailable && (
                <Badge variant="light" color="gray" size="xs" style={{ flexShrink: 0 }}>
                  Inactive
                </Badge>
              )}
            </Group>
            {(subtitle || displayId) && (
              <Text size="xs" c="dimmed" truncate ff={subtitle ? undefined : 'monospace'}>
                {subtitle ?? displayId}
              </Text>
            )}
          </Box>

          {kindLabel && (
            <Badge variant="light" color={kindColor ?? 'gray'} size="sm" style={{ flexShrink: 0 }}>
              {kindLabel}
            </Badge>
          )}
        </Group>

        <Group gap={4} wrap="nowrap">
          {onNavigate && !unavailable && (
            <Tooltip label="Open" openDelay={400}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={`Open ${title}`}
                onClick={() => onNavigate(id)}
              >
                <IconExternalLink size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Remove from category" openDelay={400}>
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              aria-label={`Remove ${title}`}
              onClick={() => onRemove(id)}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Box>
  );
}

export const SortableChildItem = memo(SortableChildItemInner) as typeof SortableChildItemInner;
